import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Package, ArrowRight, Linkedin, Instagram, Facebook, Twitter } from "lucide-react";
import { NAV, CATEGORIES, INDUSTRIES, BRAND } from "@/data/catalog";
import { subscribeNewsletter } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await subscribeNewsletter(email);
      toast.success("Subscribed! Watch your inbox for packaging insights.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-navy text-white mt-24">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
          <div>
            <div className="flex items-center gap-2 mb-5">
              <span className="grid place-items-center h-9 w-9 rounded-xl bg-white text-navy">
                <Package size={18} />
              </span>
              <span className="font-heading text-xl font-extrabold">ElitzPacks</span>
            </div>
            <p className="text-white/60 max-w-xs leading-relaxed text-sm">{BRAND.tagline}. Manufacturer & wholesale supplier of complete packaging solutions.</p>
            <div className="flex gap-3 mt-6">
              {[Linkedin, Instagram, Facebook, Twitter].map((Icon, i) => (
                <a key={i} href="#" aria-label="social" data-testid={`social-${i}`} className="grid place-items-center h-9 w-9 rounded-full border border-white/15 hover:bg-white hover:text-navy transition-colors">
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="overline text-white/50 mb-4">Products</h4>
            <ul className="space-y-2.5">
              {CATEGORIES.slice(0, 6).map((c) => (
                <li key={c.slug}>
                  <Link to={`/products?category=${c.slug}`} className="text-sm text-white/70 hover:text-white transition-colors">{c.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="overline text-white/50 mb-4">Company</h4>
            <ul className="space-y-2.5">
              {NAV.filter((n) => n.label !== "Products").map((n) => (
                <li key={n.label}>
                  <Link to={n.to} className="text-sm text-white/70 hover:text-white transition-colors">{n.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="overline text-white/50 mb-4">Newsletter</h4>
            <p className="text-sm text-white/60 mb-4">Packaging tips & trends, once a month.</p>
            <form onSubmit={submit} className="flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="newsletter-input"
                className="flex-1 min-w-0 rounded-full bg-white/10 border border-white/15 px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-royal"
              />
              <button type="submit" disabled={busy} data-testid="newsletter-submit" className="grid place-items-center h-11 w-11 rounded-full bg-emerald text-white hover:scale-105 transition-transform disabled:opacity-50">
                <ArrowRight size={17} />
              </button>
            </form>
            <div className="mt-6 text-xs text-white/50 space-y-1">
              <p>{BRAND.phone}</p>
              <p>{BRAND.email}</p>
              <p>{BRAND.hours}</p>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/50">
          <p>© {new Date().getFullYear()} ElitzPacks. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <span>ISO 9001 · FSSAI · FSC</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
